import React, { useState, useEffect, useRef } from 'react';
import '../styles/DataEmbeddingsPage.css';
import EmbeddingStatusIndicator from '../components/EmbeddingStatusIndicator';
import { useGroup } from '../contexts/GroupContext';
import { 
  API_BASE_URL, 
  getAuthHeaders, 
  getEmbeddingRequests, 
  requestEmbedding, 
  getEmbeddingSummary 
} from '../utils/api';
import { 
  enhancedFetch, 
  clearErrorAfterDelay,
  generateErrorMessage 
} from '../utils/errorHandling';
import {
  getEffectivePollInterval,
  initActivityTracking,
  onActivityTierReset,
} from '../utils/activityAwarePolling';

function DataEmbeddingsPage() {
  const { activeSuffix } = useGroup();
  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [embeddingStatuses, setEmbeddingStatuses] = useState(new Map());
  const [message, setMessage] = useState({ type: '', text: '' });
  const errorTimeoutRef = useRef(null);
  
  // Recent requests state
  const [recentRequests, setRecentRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  
  // Polling: list-only, 20s when active / 120s when idle, only when tab visible
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const pollingErrorsRef = useRef(0);
  const pollTimeoutRef = useRef(null);
  const lastPollBaseMsRef = useRef(120000);
  const POLL_ACTIVE_MS = 20000;
  const POLL_IDLE_MS = 120000;

  const scheduleNextPoll = (delayMs) => {
    if (typeof document !== 'undefined' && document.hidden) return;
    lastPollBaseMsRef.current = delayMs;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    pollTimeoutRef.current = setTimeout(() => {
      pollTimeoutRef.current = null;
      loadRecentRequests();
    }, getEffectivePollInterval(delayMs));
  };

  // Load tools on mount; start list-only polling (visibility-aware)
  useEffect(() => {
    initActivityTracking();
    loadTools();
    loadRecentRequests();

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      } else {
        loadRecentRequests();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const unsubscribeTier = onActivityTierReset(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      scheduleNextPoll(lastPollBaseMsRef.current || POLL_IDLE_MS);
    });

    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      unsubscribeTier();
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSuffix]);

  const loadTools = async () => {
    setToolsLoading(true);
    try {
      const url = new URL(`${API_BASE_URL}/casc/v1/api/vector/tools`);
      url.searchParams.set('page', '1');
      url.searchParams.set('pageSize', '100');

      const authHeaders = await getAuthHeaders();
      const data = await enhancedFetch(url.toString(), {
        method: 'GET',
        headers: authHeaders
      });

      if (data.success && data.items) {
        const normalizedItems = (data.items || []).map((t) => {
          const groupSlug = t?.ingestion_group_slug || t?.metadata?.group_slug || t?._context?.groupSlug || '';
          return { ...t, ingestion_group_slug: groupSlug };
        });
        setTools(normalizedItems);
        
        // Load embedding statuses for tools
        if (normalizedItems.length > 0) {
          loadEmbeddingStatuses(normalizedItems);
        }
      }
    } catch (error) {
      console.error('Error loading tools:', error);
      const errorMessage = generateErrorMessage(error, 'loading tools', error.response);
      setMessage({ type: 'error', text: errorMessage });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 8000);
    } finally {
      setToolsLoading(false);
    }
  };

  const loadRecentRequests = async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    setRequestsLoading(true);
    try {
      const response = await getEmbeddingRequests(null, undefined);
      if (response.success && response.requests) {
        const sortedRequests = (response.requests || [])
          .sort((a, b) => {
            const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
            const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
            return bTime - aTime;
          })
          .slice(0, 50);
        setRecentRequests(sortedRequests);
        pollingErrorsRef.current = 0;
        setConnectionStatus('connected');
        const hasActive = sortedRequests.some(r => r.status === 'created' || r.status === 'processing');
        scheduleNextPoll(hasActive ? POLL_ACTIVE_MS : POLL_IDLE_MS);
      } else {
        scheduleNextPoll(POLL_IDLE_MS);
      }
    } catch (error) {
      console.error('Error loading recent requests:', error);
      pollingErrorsRef.current += 1;
      if (pollingErrorsRef.current >= 3) setConnectionStatus('error');
      else setConnectionStatus('retrying');
      scheduleNextPoll(POLL_IDLE_MS);
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadEmbeddingStatuses = async (toolsList) => {
    if (!toolsList || toolsList.length === 0) return;
    
    try {
      const provisional = new Map();
      toolsList.forEach(tool => {
        if (!tool?.id) return;
        provisional.set(tool.id, { status: 'none', request: null, error: null });
      });

      // Fetch summaries
      const summaries = await Promise.all(
        toolsList.map(async (tool) => {
          if (!tool?.id || !tool?.search_field) return { id: tool?.id, ok: false };
          try {
            const group = tool?.ingestion_group_slug;
            const sum = await getEmbeddingSummary({ key: tool.search_field, value: '', group });
            return { id: tool.id, ok: true, summary: sum };
          } catch (_) {
            return { id: tool.id, ok: false };
          }
        })
      );

      summaries.forEach(({ id, ok, summary }) => {
        if (!id) return;
        if (ok && summary && summary.needsReembed) {
          provisional.set(id, {
            status: 'stale',
            request: null,
            error: null,
            total: summary.total,
            embedded: summary.embedded
          });
        } else if (ok && summary && summary.lastEmbeddedAt) {
          provisional.set(id, {
            status: 'finished',
            request: null,
            error: null,
            total: summary.total,
            embedded: summary.embedded
          });
        }
      });

      // Fetch active requests to refine statuses
      const response = await getEmbeddingRequests();
      if (response.success && response.requests) {
        const newStatuses = new Map(provisional);

        toolsList.forEach(tool => {
          if (!tool?.id) return;
          const computed = computeStatusForTool(tool, response.requests);
          if (computed.status !== 'none') {
            const current = newStatuses.get(tool.id);
            if (
              computed.status === 'finished' &&
              current?.status === 'stale'
            ) {
              newStatuses.set(tool.id, {
                ...computed,
                status: 'stale',
                total: current.total,
                embedded: current.embedded,
              });
            } else {
              newStatuses.set(tool.id, {
                ...computed,
                total: current?.total,
                embedded: current?.embedded,
              });
            }
          }
        });

        setEmbeddingStatuses(newStatuses);
      } else {
        setEmbeddingStatuses(provisional);
      }
    } catch (error) {
      console.error('Error loading embedding statuses:', error);
    }
  };

  const computeStatusForTool = (tool, requests) => {
    if (!tool || !tool.id) return { status: 'none', request: null, error: null };
    
    const searchKey = tool.search_field;
    const groupSlug = tool?.ingestion_group_slug;
    const list = Array.isArray(requests) ? requests : [];

    const matches = list.filter(r => {
      const sameKey = !searchKey || r?.key === searchKey;
      const sameGroup = !groupSlug || r?.metadata?.group_slug === groupSlug;
      return sameKey && sameGroup;
    });

    if (!matches || matches.length === 0) {
      return { status: 'none', request: null, error: null };
    }

    const parseTime = (r) => new Date(r?.updated_at || r?.created_at || 0).getTime();
    const active = matches.filter(r => r?.status === 'created' || r?.status === 'processing').sort((a, b) => parseTime(b) - parseTime(a));
    
    if (active.length > 0) {
      const req = active[0];
      return { status: req.status, request: req, error: null };
    }

    const finished = matches.filter(r => r?.status === 'finished').sort((a, b) => parseTime(b) - parseTime(a));
    if (finished.length > 0) {
      return { status: 'finished', request: finished[0], error: null };
    }

    const errored = matches.filter(r => r?.status === 'errored').sort((a, b) => parseTime(b) - parseTime(a));
    if (errored.length > 0) {
      return { status: 'errored', request: errored[0], error: 'Embedding failed' };
    }

    return { status: 'none', request: null, error: null };
  };

  const handleInitiateEmbedding = async (tool) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    try {
      const group = tool?.ingestion_group_slug;
      if (!group) {
        setMessage({ type: 'error', text: 'Tool must have an ingestion group before initiating embeddings.' });
        errorTimeoutRef.current = clearErrorAfterDelay(() => {
          setMessage({ type: '', text: '' });
        }, 6000);
        return;
      }

      const response = await requestEmbedding(tool.id, group);
      
      if (response.success && response.request) {
        setMessage({ 
          type: 'success', 
          text: `Embedding request created for "${tool.name}"` 
        });
        
        // Refresh data
        loadTools();
        loadRecentRequests();
        
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        throw new Error(response.error || 'Failed to create embedding request');
      }
    } catch (error) {
      console.error('Error initiating embedding:', error);
      const errorMessage = generateErrorMessage(error, 'creating embedding request', error.response);
      setMessage({ type: 'error', text: errorMessage });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 8000);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this embedding request?')) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/casc/v1/api/embedding/${requestId}/cancel`;
      const authHeaders = await getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Request cancelled successfully' });
        loadRecentRequests();
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        throw new Error(data.error || 'Failed to cancel request');
      }
    } catch (error) {
      console.error('Error cancelling request:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to cancel request' });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 6000);
    }
  };

  const handleResetRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to reset this request? It will be requeued for processing.')) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/casc/v1/api/embedding/${requestId}/reset`;
      const authHeaders = await getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Request reset successfully' });
        loadRecentRequests();
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        throw new Error(data.error || 'Failed to reset request');
      }
    } catch (error) {
      console.error('Error resetting request:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to reset request' });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 6000);
    }
  };

  const handleClearError = async (requestId) => {
    try {
      const url = `${API_BASE_URL}/casc/v1/api/tools/embedding/request/${requestId}/clear-error`;
      const authHeaders = await getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Error cleared from notifications' });
        loadRecentRequests();
        // Trigger notifications refresh
        window.dispatchEvent(new Event('refreshStats'));
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        throw new Error(data.error || 'Failed to clear error');
      }
    } catch (error) {
      console.error('Error clearing embedding error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to clear error' });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 6000);
    }
  };

  const toggleRequestSelection = (requestId) => {
    setSelectedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const toggleErrorExpansion = (requestId) => {
    setExpandedErrors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const handleBulkCancel = async () => {
    if (selectedRequests.size === 0) return;
    
    if (!window.confirm(`Cancel ${selectedRequests.size} selected request(s)?`)) {
      return;
    }

    const promises = Array.from(selectedRequests).map(id => handleCancelRequest(id));
    await Promise.all(promises);
    setSelectedRequests(new Set());
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatDuration = (startDate, endDate = null) => {
    if (!startDate) return null;
    try {
      const start = new Date(startDate).getTime();
      if (Number.isNaN(start)) return null;
      const end = endDate ? new Date(endDate).getTime() : Date.now();
      const endMs = Number.isNaN(end) ? Date.now() : end;
      const diff = Math.max(0, endMs - start);
      const seconds = Math.floor(diff / 1000);

      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ${minutes % 60}m`;
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    } catch {
      return null;
    }
  };

  const requestDurationText = (request) => {
    if (!request?.created_at) return null;
    const status = String(request.status || '').toLowerCase();
    const isTerminal = status === 'finished' || status === 'errored' || status === 'canceled' || status === 'cancelled';
    // Finished jobs must use completion time (updated_at), not "age since created" (now - created_at).
    const end = isTerminal ? (request.updated_at || request.created_at) : null;
    return formatDuration(request.created_at, end);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'created': return 'status-badge-created';
      case 'processing': return 'status-badge-processing';
      case 'finished': return 'status-badge-finished';
      case 'errored': return 'status-badge-errored';
      case 'canceled': return 'status-badge-canceled';
      default: return 'status-badge-none';
    }
  };

  return (
    <div className="data-embeddings-page">
      <div className="embeddings-header">
        <h2><i className="fas fa-vector-square"></i> Data Embeddings Management</h2>
        <p className="embeddings-description">
          Monitor and manage vector embeddings for your data. Embeddings enable semantic search and AI-powered data retrieval.
        </p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          <div className="alert-content">
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            <span className="alert-text">{message.text}</span>
          </div>
        </div>
      )}

      {connectionStatus !== 'connected' && (
        <div className={`alert alert-${connectionStatus === 'retrying' ? 'warning' : 'error'}`} style={{ marginBottom: '20px' }}>
          <div className="alert-content">
            <i className={`fas fa-${connectionStatus === 'retrying' ? 'exclamation-triangle' : 'wifi'}`}></i>
            <span className="alert-text">
              {connectionStatus === 'retrying' 
                ? `Connection issue detected. Retrying...` 
                : `Connection error. Retrying with backoff...`}
            </span>
          </div>
        </div>
      )}

      {/* Tools Section */}
      <div className="embeddings-section">
        <h3><i className="fas fa-tools"></i> Embedding Tools</h3>
        {toolsLoading ? (
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i> Loading tools...
          </div>
        ) : tools.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No embedding tools configured. Create tools in the Tools page to enable embeddings.</p>
          </div>
        ) : (
          <div className="tools-grid">
            {tools.map(tool => {
              const statusInfo = embeddingStatuses.get(tool.id) || { status: 'none', request: null };
              const isActive = statusInfo.status === 'created' || statusInfo.status === 'processing';
              
              return (
                <div key={tool.id} className="tool-card">
                  <div className="tool-card-header">
                    <h4>{tool.name}</h4>
                    <span className="tool-slug">{tool.slug}</span>
                  </div>
                  <div className="tool-meta">
                    <div className="tool-meta-item">
                      <strong>Group:</strong> {tool.ingestion_group_slug || 'None'}
                    </div>
                    <div className="tool-meta-item">
                      <strong>Field:</strong> {tool.search_field || 'Not set'}
                    </div>
                    <div className="tool-meta-item">
                      <strong>Status:</strong>
                      <EmbeddingStatusIndicator 
                        status={statusInfo.status}
                        error={statusInfo.error}
                      />
                    </div>
                    {statusInfo.total !== undefined && statusInfo.embedded !== undefined && (
                      <div className="tool-meta-item">
                        <strong>Progress:</strong> {statusInfo.embedded} / {statusInfo.total} embedded
                      </div>
                    )}
                  </div>
                  <div className="tool-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleInitiateEmbedding(tool)}
                      disabled={isActive || !tool.ingestion_group_slug}
                      title={!tool.ingestion_group_slug ? 'Set an ingestion group first' : 'Start embedding'}
                    >
                      <i className="fas fa-play"></i> {isActive ? 'Processing...' : 'Initiate'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Requests Section */}
      <div className="embeddings-section">
        <div className="section-header">
          <h3><i className="fas fa-list"></i> Recent Embedding Requests</h3>
          {selectedRequests.size > 0 && (
            <button 
              className="btn btn-sm btn-danger"
              onClick={handleBulkCancel}
            >
              <i className="fas fa-ban"></i> Cancel Selected ({selectedRequests.size})
            </button>
          )}
        </div>
        
        {requestsLoading ? (
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i> Loading requests...
          </div>
        ) : recentRequests.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No embedding requests found.</p>
          </div>
        ) : (
          <div className="requests-table-container">
            <table className="requests-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input 
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRequests(new Set(recentRequests.map(r => r.id)));
                        } else {
                          setSelectedRequests(new Set());
                        }
                      }}
                      checked={selectedRequests.size === recentRequests.length && recentRequests.length > 0}
                    />
                  </th>
                  <th>Status</th>
                  <th>Field</th>
                  <th>Group</th>
                  <th>Progress</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentRequests.map(request => {
                  const metadata = request.metadata || {};
                  const total = metadata.total || 0;
                  const remaining = metadata.remaining || 0;
                  const completed = Math.max(0, total - remaining);
                  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
                  const isActive = request.status === 'created' || request.status === 'processing';
                  const isStuck = isActive && formatDuration(request.created_at)?.includes('h');
                  const hasErrors = metadata.errors && metadata.errors.length > 0;
                  const errorMessage = metadata.error || (hasErrors ? metadata.errors[metadata.errors.length - 1] : null);
                  const isExpanded = expandedErrors.has(request.id);
                  const showErrorToggle = errorMessage && (request.status === 'errored' || (request.status === 'processing' && (hasErrors || metadata.error)));

                  return (
                    <React.Fragment key={request.id}>
                      <tr className={isStuck ? 'request-stuck' : ''}>
                      <td>
                        <input 
                          type="checkbox"
                          checked={selectedRequests.has(request.id)}
                          onChange={() => toggleRequestSelection(request.id)}
                        />
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                          {request.status}
                          {isStuck && <i className="fas fa-exclamation-triangle" title="Possibly stuck — check error details or use Reset to retry"></i>}
                        </span>
                        {showErrorToggle && (
                          <button
                            className="btn-error-toggle"
                            onClick={() => toggleErrorExpansion(request.id)}
                            title={isExpanded ? "Hide error details" : "Show error details"}
                          >
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                          </button>
                        )}
                      </td>
                      <td>
                        <code>{request.key}</code>
                      </td>
                      <td>{metadata.group_slug || '—'}</td>
                      <td>
                        {total > 0 ? (
                          <div className="progress-cell">
                            <div className="progress-bar-mini">
                              <div 
                                className="progress-bar-mini-fill" 
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{completed}/{total} ({percentage}%)</span>
                          </div>
                        ) : '—'}
                        {hasErrors && (
                          <span className="error-indicator" title={`${metadata.errors.length} errors`}>
                            <i className="fas fa-exclamation-circle"></i> {metadata.errors.length}
                          </span>
                        )}
                      </td>
                      <td>{formatDate(request.created_at)}</td>
                      <td>{requestDurationText(request) || '—'}</td>
                      <td>
                        {isActive && (
                          <>
                            <button
                              className="btn btn-xs btn-danger"
                              onClick={() => handleCancelRequest(request.id)}
                              title="Cancel request"
                            >
                              <i className="fas fa-ban"></i>
                            </button>
                            {isStuck && (
                              <button
                                className="btn btn-xs btn-warning"
                                onClick={() => handleResetRequest(request.id)}
                                title="Reset stuck request"
                                style={{ marginLeft: '4px' }}
                              >
                                <i className="fas fa-redo"></i>
                              </button>
                            )}
                          </>
                        )}
                        {request.status === 'errored' && (
                          <>
                            <button
                              className="btn btn-xs btn-primary"
                              onClick={() => handleResetRequest(request.id)}
                              title="Retry failed request"
                            >
                              <i className="fas fa-redo"></i>
                            </button>
                            {!request.metadata?.error_cleared && (
                              <button
                                className="btn btn-xs btn-clear-error"
                                onClick={() => handleClearError(request.id)}
                                title="Clear error from notifications"
                                style={{ marginLeft: '4px' }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                            {request.metadata?.error_cleared && (
                              <span className="cleared-badge" title="Error cleared from notifications">
                                <i className="fas fa-check-circle"></i> Cleared
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                    {showErrorToggle && isExpanded && errorMessage && (
                      <tr className="error-details-row">
                        <td colSpan="8">
                          <div className="error-details-container">
                            <div className="error-details-header">
                              <i className="fas fa-exclamation-circle"></i>
                              <strong>{request.status === 'processing' ? 'Last error (request still processing)' : 'Error Details'}</strong>
                            </div>
                            <div className="error-details-content">
                              {errorMessage}
                            </div>
                            {hasErrors && metadata.errors.length > 1 && (
                              <div className="error-details-list">
                                <strong>All Errors ({metadata.errors.length}):</strong>
                                <ul>
                                  {metadata.errors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {request.status === 'processing' && (
                              <div className="hint" style={{ marginTop: 8 }}>
                                If this request is stuck, check server logs or use <strong>Reset</strong> to retry.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataEmbeddingsPage;
